import React, { forwardRef, useImperativeHandle, useState } from "react";
import { startPlaybackStream, startVideoStream, stopVideoStream } from "../../services/vehicleMediaService";
import VideoJSPlayer from "../videoplayer/videoPlayer";
import Player from "video.js/dist/types/player";

export type FlespiVideoStreamPlayerHandle = {
    openLiveStream: (flespiDeviceId: string, channelIdx: number) => void;
    startPlaybackStream: (
        flespiDeviceId: string,
        channelIdx: number,
        from: Date,
        duration?: number
    ) => void;
    closeLiveStream: (flespiDeviceId: string, channelIdx: number) => void;
};

export interface FlespiVideoStreamPlayerProps {
    onLoadingChange?: (loading: boolean) => void;
    onPlayerReady?: (player: Player) => void;
    // If needed we can pass in actual video.js player options.
    // for now they are fixed in FlespiVideoStreamPlayer to keep usage simple
    onStreamOperationError?: (operation: FlespiPlayerOperation, error: any) => void;
    onStreamOperationException?: (operation: FlespiPlayerOperation, message: string, ex: any) => void;
}

// Callback helper to start live stream
export const openLiveStream = (
    playerRef: React.MutableRefObject<FlespiVideoStreamPlayerHandle>,
    deviceId: string,
    channelIdx: number
) => {
    playerRef.current?.openLiveStream(deviceId, channelIdx);
};

// Callback helper to start playback
export const openPlaybackStream = (
    playerRef: React.MutableRefObject<FlespiVideoStreamPlayerHandle>,
    deviceId: string,
    channelIdx: number,
    from: Date,
    durationInSeconds: number) => {
    playerRef.current?.startPlaybackStream(
        deviceId,
        channelIdx,
        from,
        durationInSeconds
    );
};

// Callback helper to close stream
export const closeLiveStream = (
    playerRef: React.MutableRefObject<FlespiVideoStreamPlayerHandle>,
    deviceId: string,
    channelIdx: number) => {
    playerRef.current?.closeLiveStream(
        deviceId,
        channelIdx);
};

export enum FlespiPlayerOperation {
    startLiveStream,
    closeStream,
    playbackStream
}

export const FlespiVideoStreamPlayer = forwardRef<
    FlespiVideoStreamPlayerHandle,
    FlespiVideoStreamPlayerProps
>((props, ref) => {

    const [currentVideoUrl, setCurrentVideoUrl] = useState<string>(null);
    const [closed, setClosed] = useState<boolean>(true);

    const playerRef = React.useRef(null);

    const setLoading = (loading: boolean) => {
        console.debug("Stream loading state:", loading);

        if (props.onLoadingChange) {
            props.onLoadingChange(loading);
        }
    };

    const onApiError = (operation: FlespiPlayerOperation, message: any) => {

        if (props.onStreamOperationError) {
            props.onStreamOperationError(operation, message);
        }
    };

    const onApiException = (operation: FlespiPlayerOperation, message: string, ex: any) => {

        console.error(operation, ex);
        if (props.onStreamOperationException) {
            props.onStreamOperationException(operation, message, ex);
        }
    };

    const onOpenLiveStream = (flespiDeviceId: string, channelIdx: number) => {
        setLoading(true);
        setCurrentVideoUrl(null);
        setClosed(false);
        console.debug("Starting live stream for:", flespiDeviceId, " Channel:", channelIdx);

        startVideoStream(
            flespiDeviceId,
            channelIdx
        ).then((res) => {
            if (res.status !== 200 || res.data?.statusCode !== 200) {
                onApiError(FlespiPlayerOperation.startLiveStream, "Stream failed to start");
                console.error("Unable to start live stream", res.statusText, res.data?.statusCode, res.data?.error);
                return;
            }

            if (res.data?.result[0]?.response?.data !== null) {
                const hlsUrl = res.data.result[0].response.data.hls as string;

                console.debug("HLS Stream url to play:", hlsUrl);
                setCurrentVideoUrl(hlsUrl);

            } else {
                console.error("Unable to start video", res.data?.result[0]?.response?.text ?? "");
                onApiError(FlespiPlayerOperation.startLiveStream, res.data?.result[0]?.response?.text ?? "");
            }
        }).catch((e) => {
            onApiException(FlespiPlayerOperation.startLiveStream, "Stream failed to start", e);
        }).finally(() => {
            setLoading(false);
        });
    };

    const onStartPlaybackStream = (flespiDeviceId: string, channelIdx: number, from: Date, duration: number = 300) => {

        console.debug("Starting playback stream for:", flespiDeviceId, " Channel:", channelIdx, " From:", from, "Duration: ", duration);
        setLoading(true);
        setCurrentVideoUrl(null);
        setClosed(false);

        startPlaybackStream(
            flespiDeviceId,
            channelIdx,
            from,
            duration
        ).then((res) => {
            console.debug("Start video playback response received");
            if (res.status !== 200 || res.data?.statusCode !== 200) {
                console.error("Unable to start videoplayback stream", res.statusText, res.data?.statusCode, res.data?.error);
                return;
            }

            if (res.data?.result[0]?.response?.data !== null) {
                const hlsUrl = res.data.result[0].response.data.hls as string;
                console.debug("HLS Stream url to play:", hlsUrl);
                setCurrentVideoUrl(hlsUrl);

            } else {
                console.error("Unable to start video", res.data?.result[0]?.response?.data?.text ?? "");
                onApiError(FlespiPlayerOperation.playback, res.data?.result[0]?.response?.text ?? "");
            }
        }).catch((e) => {
            onApiException(FlespiPlayerOperation.playback, "Stream failed to start", e);
        }).finally(() => {
            setLoading(false);
        });
    };

    const onCloseLiveStream = (flespiDeviceId: string, channelIdx: number) => {
        console.debug("Stopping live stream for:", flespiDeviceId, " Channel:", channelIdx);
        setCurrentVideoUrl(null);
        setClosed(true);
        stopVideoStream(
            flespiDeviceId,
            channelIdx)
            .then((res) => {

                if (res.status !== 200 || res.data?.statusCode !== 200) {
                    console.error("Unable to stop video stream", res.statusText, res.data?.statusCode, res.data?.error);
                    onApiError(FlespiPlayerOperation.closeStream, "Stream failed to stop");
                } else {
                    console.debug("Live stream stopped", res.data?.result[0]?.response?.data?.text ?? "");
                }
            })
            .catch((e) => {
                onApiException(FlespiPlayerOperation.closeStream, "Stream failed to stop", e);
            });
    };

    // Video player on ready handler
    const handlePlayerReady = (player: Player) => {
        playerRef.current = player;

        if (props.onPlayerReady) {
            props.onPlayerReady(player);
        }

        // You can handle player events here, for example:
        player.on("waiting", () => {
            console.debug("player is waiting");
        });

        player.on("dispose", () => {
            console.debug("player will dispose");
        });
    };

    // 👇 THIS IS THE KEY PART
    useImperativeHandle(ref, () => ({
        openLiveStream: onOpenLiveStream,
        startPlaybackStream: onStartPlaybackStream,
        closeLiveStream: onCloseLiveStream
    }));

    return (
        <>
            {
                currentVideoUrl && !closed &&
                <VideoJSPlayer
                    options={{
                        muted: true,
                        autoplay: true,
                        controls: true,
                        liveui: true, // UI for live streams
                        fluid: true,
                        html5: {
                            hls: {
                                enableLowInitialPlaylist: true,
                                overrideNative: true, // ensures playback in Chrome/Firefox
                            },
                        },
                        sources: [
                            {
                                src: currentVideoUrl,
                                type: "application/x-mpegURL",
                            },
                        ],
                    }}
                    onReady={handlePlayerReady}
                />
            }
        </>
    );
});
